import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

import gestaoCarteiraIcon from './investment-portfolio.svg';
import logo from './kollecta.io.png';
import databaseIcon from './database.svg';
import settingIcon from './setting.svg';

import style from './style.module.css';

const PORTFOLIO_PATHS = ['/dashboard/home', '/dashboard/manager-pastdues', '/dashboard/campaign-history'];
const SUBMENU_ENGAGE_KEY = 'kollecta_portfolio_submenu_engage';

export function Sidebar() {
  const location = useLocation();
  const [portfolioFlyoutOpen, setPortfolioFlyoutOpen] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);
  const [submenuEngaged, setSubmenuEngaged] = useState(
    () => sessionStorage.getItem(SUBMENU_ENGAGE_KEY) === '1',
  );
  const [userType, setUserType] = useState('operator');
  const portfolioRef = useRef(null);

  useEffect(() => {
    setSubmenuEngaged(sessionStorage.getItem(SUBMENU_ENGAGE_KEY) === '1');
  }, [location.pathname]);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      const userParse = JSON.parse(user);
      setUserType(userParse.userType || 'operator');
    }
  }, []);

  const isPortfolioSectionActive = PORTFOLIO_PATHS.includes(location.pathname);
  const isWideSidebar = isPortfolioSectionActive && sidebarHover;

  const markPortfolioSubmenuEngaged = () => {
    sessionStorage.setItem(SUBMENU_ENGAGE_KEY, '1');
    setSubmenuEngaged(true);
  };

  const isSublinkActive = (path) => {
    if (location.pathname !== path) return false;
    if (path === '/dashboard/home' && !submenuEngaged) return false;
    return true;
  };

  useEffect(() => {
    setPortfolioFlyoutOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!portfolioFlyoutOpen) return;
    const close = (e) => {
      if (portfolioRef.current && !portfolioRef.current.contains(e.target)) {
        setPortfolioFlyoutOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [portfolioFlyoutOpen]);

  const subLinks = [
    { to: '/dashboard/home', label: 'Indicadores', restricted: false },
    { to: '/dashboard/manager-pastdues', label: 'Pesquisar clientes', restricted: false },
    { to: '/dashboard/campaign-history', label: 'Histórico de acionamentos', restricted: true },
  ].filter((link) => !link.restricted || userType !== 'operator');

  return (
    <nav
      className={`${style.navbar} ${isWideSidebar ? style['navbar-expanded'] : ''}`}
      onMouseEnter={() => setSidebarHover(true)}
      onMouseLeave={() => setSidebarHover(false)}
    >
      <div className={style.logo}>
        <img src={logo} alt="Kollecta.io" />
      </div>
      <ul className={style['navbar-nav']}>
        <li
          ref={portfolioRef}
          className={`${style['nav-item']} ${style['portfolio-group']} ${
            portfolioFlyoutOpen ? style['portfolio-flyout-visible'] : ''
          }`}
        >
          {isPortfolioSectionActive && sidebarHover ? (
            <div className={style['portfolio-card']}>
              <div className={style['portfolio-header']}>
                <img src={gestaoCarteiraIcon} alt="" className={style['portfolio-header-icon']} />
                <span className={style['portfolio-title']}>Gestão de carteira</span>
              </div>
              <ul className={style['portfolio-subnav']}>
                {subLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      onClick={markPortfolioSubmenuEngaged}
                      className={`${style['portfolio-sublink']} ${
                        isSublinkActive(link.to) ? style['portfolio-sublink-active'] : ''
                      }`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : isPortfolioSectionActive ? (
            <div className={style['portfolio-collapsed-on-route']} aria-hidden="true">
              <span className={style['portfolio-icon-slot-on-route']}>
                <img src={gestaoCarteiraIcon} alt="" />
              </span>
            </div>
          ) : (
            <>
              <div className={style['portfolio-trigger-wrap']}>
                <button
                  type="button"
                  className={`${style['portfolio-trigger']} ${
                    portfolioFlyoutOpen ? style['portfolio-trigger-open'] : ''
                  }`}
                  aria-expanded={portfolioFlyoutOpen}
                  aria-haspopup="true"
                  onClick={() => setPortfolioFlyoutOpen((v) => !v)}
                >
                  <span className={style['portfolio-icon-slot']}>
                    <img src={gestaoCarteiraIcon} alt="" />
                  </span>
                </button>
                <span className={style['portfolio-tooltip']}>Gestão de carteira</span>
              </div>
              {portfolioFlyoutOpen && (
                <div className={style['portfolio-flyout']} role="menu">
                  <div className={style['flyout-header']}>
                    <span>Gestão de carteira</span>
                  </div>
                  <div className={style['flyout-card']}>
                    {subLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        role="menuitem"
                        className={`${style['flyout-link']} ${
                          isSublinkActive(link.to) ? style['flyout-link-active'] : ''
                        }`}
                        onClick={() => {
                          markPortfolioSubmenuEngaged();
                          setPortfolioFlyoutOpen(false);
                        }}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </li>
        <li
          className={`${style['nav-item']} ${style['nav-item--tooltip']} ${
            location.pathname === '/dashboard/past-due' ? style.active : ''
          }`}
        >
          <div className={style['nav-link-tooltip-wrap']}>
            <Link to="/dashboard/past-due" className={style['nav-link']}>
              <span className={style['nav-link-icon-slot']}>
                <img src={databaseIcon} alt="" />
              </span>
              <span className={style['link-text']}>Importação de dados</span>
            </Link>
            <span className={style['nav-tooltip']} role="tooltip">
              Importação de dados
            </span>
          </div>
        </li>
        {userType !== 'operator' && (
          <li
            className={`${style['nav-item']} ${style['nav-item--tooltip']} ${
              location.pathname === '/dashboard/communication-dispatcher' ? style.active : ''
            }`}
          >
            <div className={style['nav-link-tooltip-wrap']}>
              <Link to="/dashboard/communication-dispatcher" className={style['nav-link']}>
                <span className={style['nav-link-icon-slot']}>
                  <img src={settingIcon} alt="" />
                </span>
                <span className={style['link-text']}>Configurações</span>
              </Link>
              <span className={style['nav-tooltip']} role="tooltip">
                Configurações
              </span>
            </div>
          </li>
        )}
      </ul>
    </nav>
  );
}
